import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Button,
  DropdownSearch,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/common';
import { AppRoutes } from '@/constants';
import { navigate } from '@/providers';
import { handleServerValidationErrors, parseNumber, showAlertSuccess } from '@/utils';

import {
  DEVICE_TYPE_OPTIONS,
  IPAD_CONFIG_CREATION_SCHEMA,
  IPAD_CONFIG_FORM_FIELDS,
  IPAD_CONFIG_REMARKS_MAX_LENGTH,
} from '@/features/ipad-configs/constants';
import { IIpadConfigUpdate } from '@/features/ipad-configs/interfaces';
import { useIpadConfigMutation } from '@/features/ipad-configs/hooks';
import { mapSerialNumberResponseToDropdownSearchResponse } from '@/features/ipad-configs/utils';
import { IpadConfigService } from '@/features/ipad-configs/services';
import { DeviceType } from '@/features/ipad-configs/enums';
import ProjectRoomSection from './ProjectRoomSection';

const IpadConfigCreation = () => {
  const mutation = useIpadConfigMutation();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
  } = useForm<IIpadConfigUpdate>({
    resolver: yupResolver(IPAD_CONFIG_CREATION_SCHEMA),
  });
  const deviceTypeValue = watch(IPAD_CONFIG_FORM_FIELDS.deviceType);

  const onSubmit = async (data: IIpadConfigUpdate) => {
    await mutation.mutateAsync(data, {
      onSuccess: () => {
        navigate(AppRoutes.IPAD_CONFIGURATIONS);
        showAlertSuccess('iPad configured successfully!');
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
  };

  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Header with Title */}
      <h1 className="text-h1 font-bold">Create New Configuration</h1>

      <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
        <div className="text-sm flex flex-col gap-y-6 p-m">
          <div className="text-xl font-bold text-brand-primary-darkest">Device Details</div>
          <Controller
            name={IPAD_CONFIG_FORM_FIELDS.serialNumber}
            control={control}
            render={({ field }) => (
              <DropdownSearch
                label="iPad Serial Number"
                placeholder="Select iPad Serial Number"
                searchCallback={async (searchValue) => {
                  const deviceSearchValue = {
                    ...searchValue,
                    device_type: [DeviceType.NotInUse],
                  };
                  return mapSerialNumberResponseToDropdownSearchResponse(
                    await IpadConfigService.GetIpadSerialNumbers(deviceSearchValue),
                  );
                }}
                onSelect={field.onChange}
                required
                error={errors[IPAD_CONFIG_FORM_FIELDS.serialNumber]?.message}
              />
            )}
          />
          <Input
            label="Device Name"
            defaultValue="N/A"
            required
            disabled
            {...register(IPAD_CONFIG_FORM_FIELDS.deviceName)}
          />
          <Controller
            name={IPAD_CONFIG_FORM_FIELDS.deviceType}
            control={control}
            render={({ field }) => (
              <Select
                label="Device Type"
                required
                value={field.value?.toString()}
                onValueChange={(value) => field.onChange(parseNumber(value))}
                error={errors[IPAD_CONFIG_FORM_FIELDS.deviceType]?.message}
              >
                <SelectTrigger allowClear className="w-full">
                  <SelectValue placeholder="Select Device Type" />
                </SelectTrigger>
                <SelectContent>
                  {DEVICE_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {deviceTypeValue === DeviceType.ProjectRoom && (
            <ProjectRoomSection
              register={register}
              control={control}
              watch={watch}
              errors={errors}
            />
          )}
          <Textarea
            label="Remark"
            placeholder="Enter remarks"
            className="[&_textarea]:min-h-28"
            maxLength={IPAD_CONFIG_REMARKS_MAX_LENGTH}
            error={errors[IPAD_CONFIG_FORM_FIELDS.remarks]?.message}
            {...register(IPAD_CONFIG_FORM_FIELDS.remarks)}
          />
        </div>
      </div>
      {/* back and submit button */}
      <div className="flex justify-end gap-s-plus">
        <Button
          className="w-[104px]"
          size="lg"
          variant="cancel"
          borderRadius="soft"
          onClick={() => navigate(AppRoutes.IPAD_CONFIGURATIONS)}
        >
          Back
        </Button>
        <Button
          className="w-[104px]"
          size="lg"
          variant="primary"
          borderRadius="soft"
          onClick={handleSubmit(onSubmit)}
          disabled={mutation.isPending}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default IpadConfigCreation;
